import { modelLabel, useTranslation } from '@rhino-project/core/i18n';
import { ReactNode } from 'react';

import { useRenderPaths } from '../../hooks/renderPaths';
import { useModelCreateContext } from '@rhino-project/core/hooks';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { ModelCreateModalActions } from './ModelCreateModalActions';
import { ModelCreateSimple, ModelCreateSimpleProps } from './ModelCreateSimple';
import { ModelFieldGroup } from './ModelFieldGroup';
import { FormErrors } from '../forms/FormErrors';
import {
  RhinoPathEntries,
  RhinoResourceName,
  RhinoResourceSpecifier
} from '@rhino-project/core';
import {
  Fieldset,
  ModalBody,
  ModalBackdrop,
  ModalContainer,
  ModalDialog,
  ModalHeader,
  ModalHeading
} from '@heroui/react';

export const ModelCreateModalHeader = (props: { title?: ReactNode }) => {
  const { t } = useTranslation();
  const { model } = useModelCreateContext();
  const {
    title = t('rhino.frontend.messages.add_value', {
      value1: modelLabel(model)
    })
  } = props;

  return (
    <ModalHeader>
      <ModalHeading>{title}</ModalHeading>
    </ModalHeader>
  );
};

export const ModelCreateModalForm = ({ ...props }: { paths?: string[] }) => {
  const { paths } = useModelCreateContext();
  const renderPaths = useRenderPaths(props.paths || paths, {
    Component: ModelFieldGroup,
    props: { variant: 'secondary' }
  });

  return (
    <ModalBody>
      <Fieldset>
        <Fieldset.Group>{renderPaths}</Fieldset.Group>
      </Fieldset>
      <FormErrors />
    </ModalBody>
  );
};

const defaultComponents = {
  ModelCreateModalHeader,
  ModelCreateModalForm,
  ModelCreateModalActions
};

// `model` and `paths` are named rather than left to the index signature so the
// second is checked against the first: an attribute path the model does not
// carry is a compile error here, where the rest of the bag stays open for the
// props the overridable sub-components take.
export interface ModelCreateModalProps<
  T extends RhinoResourceSpecifier = RhinoResourceSpecifier
> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  isOpen?: boolean;
  onModalClose?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSave?: (data: any) => void;
  title?: ReactNode;
  model?: T;
  paths?: RhinoPathEntries<T>;
  [key: string]: unknown;
}

export const ModelCreateModalBase = <T extends RhinoResourceSpecifier>({
  overrides,
  isOpen = false,
  onModalClose,
  onSave,
  title,
  ...props
}: ModelCreateModalProps<T>) => {
  const {
    ModelCreateModalHeader,
    ModelCreateModalForm,
    ModelCreateModalActions
    // eslint-disable-next-line @eslint-react/static-components
  } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) onModalClose?.();
  };

  if (!isOpen) return null;

  return (
    <ModelCreateSimple
      fallback={isOpen}
      {...(props as unknown as ModelCreateSimpleProps<RhinoResourceName>)}
    >
      <ModalBackdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
        <ModalContainer>
          <ModalDialog>
            {/* eslint-disable-next-line @eslint-react/static-components */}
            <ModelCreateModalHeader title={title} />
            {/* eslint-disable-next-line @eslint-react/static-components */}
            <ModelCreateModalForm />
            {/* eslint-disable-next-line @eslint-react/static-components */}
            <ModelCreateModalActions
              onModalClose={onModalClose}
              onSave={onSave}
            />
          </ModalDialog>
        </ModalContainer>
      </ModalBackdrop>
    </ModelCreateSimple>
  );
};

export const ModelCreateModal = <T extends RhinoResourceSpecifier>(
  props: ModelCreateModalProps<T>
) =>
  useGlobalComponentForModel(
    'ModelCreateModal',
    ModelCreateModalBase as React.ComponentType<ModelCreateModalProps<T>>,
    props
  );
